@import "@fontsource/instrument-serif/latin-400.css";
@import "@fontsource/instrument-serif/latin-400-italic.css";
@import "@fontsource/jetbrains-mono/latin-400.css";
@import "@fontsource/jetbrains-mono/latin-500.css";
@import "@fontsource/jetbrains-mono/latin-600.css";
@import "@fontsource/geist/latin-300.css";
@import "@fontsource/geist/latin-400.css";
@import "@fontsource/geist/latin-500.css";
@import "@fontsource/geist/latin-600.css";
@import "@fontsource/geist/latin-700.css";

:root {
  /* base palette - warm neutral */
  --bg: #FAF8F4;
  --bg-soft: #F4F1EA;
  --bg-sunken: #EFEBE2;
  --surface: #FFFFFF;
  --surface-2: #FBF9F4;
  --line: #E6E0D2;
  --line-strong: #D4CCB8;
  --ink: #1A1814;
  --ink-2: #36322B;
  --muted: #6B645A;
  --muted-2: #948C7E;
  --faint: #B8B0A0;

  /* accent - gold/amber, restrained */
  --accent: #B8893A;
  --accent-deep: #8B6520;
  --accent-soft: #F0E4C8;
  --accent-tint: #FAF1DC;

  /* signals */
  --live: #2F8F5E;
  --live-soft: #E2F1E8;
  --hot: #C24A2C;
  --hot-soft: #F6E3DA;
  --info: #4A6B9C;

  /* radii / shadow */
  --r-xs: 3px;
  --r-sm: 5px;
  --r: 8px;
  --r-lg: 12px;
  --shadow-1: 0 1px 0 rgba(26,24,20,0.04), 0 1px 2px rgba(26,24,20,0.04);
  --shadow-2: 0 1px 0 rgba(26,24,20,0.04), 0 4px 12px -2px rgba(26,24,20,0.08);
  --shadow-pop: 0 1px 0 rgba(26,24,20,0.04), 0 12px 32px -8px rgba(26,24,20,0.18);

  /* font stacks */
  --font-ui: "Geist", -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  --font-mono: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
  --font-serif: "Instrument Serif", "Times New Roman", serif;

  --header-h: 56px;
  --rail-w: 312px;
  --right-w: 320px;
}

[data-theme="dim"] {
  --bg: #1B1A17;
  --bg-soft: #232220;
  --bg-sunken: #161513;
  --surface: #232220;
  --surface-2: #2A2926;
  --line: #34322E;
  --line-strong: #45433D;
  --ink: #F2EEE5;
  --ink-2: #D9D3C5;
  --muted: #8F887B;
  --muted-2: #6E685D;
  --faint: #4F4A42;
  --accent-soft: #3A2F18;
  --accent-tint: #28200E;
  --live-soft: #18301F;
  --hot-soft: #321A12;
  --shadow-1: 0 1px 0 rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.4);
  --shadow-2: 0 1px 0 rgba(0,0,0,0.4), 0 6px 14px rgba(0,0,0,0.4);
  --shadow-pop: 0 1px 0 rgba(0,0,0,0.4), 0 14px 36px rgba(0,0,0,0.55);
}

[data-accent="amber"] { --accent: #B8893A; --accent-deep: #8B6520; --accent-soft: #F0E4C8; --accent-tint: #FAF1DC; }
[data-accent="copper"] { --accent: #B0593A; --accent-deep: #823F26; --accent-soft: #F0D8CC; --accent-tint: #FAE8DD; }
[data-accent="ink"] { --accent: #2D3748; --accent-deep: #111827; --accent-soft: #DCE0E8; --accent-tint: #ECEEF3; }
[data-accent="olive"] { --accent: #6B7A38; --accent-deep: #4A5526; --accent-soft: #E2E6D0; --accent-tint: #F0F2E2; }

[data-density="compact"] {
  --pad-card: 12px;
  --gap-feed: 10px;
}
[data-density="comfortable"] {
  --pad-card: 16px;
  --gap-feed: 14px;
}

* { box-sizing: border-box; }
html, body, #root { height: 100%; }
html, body { margin: 0; padding: 0; }
body {
  font-family: var(--font-ui);
  font-size: 14px;
  line-height: 1.45;
  color: var(--ink);
  background: var(--bg);
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-feature-settings: "ss01", "cv11";
}

/* scrollbars */
::-webkit-scrollbar { width: 10px; height: 10px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--line-strong); border-radius: 6px; border: 2px solid var(--bg); }
::-webkit-scrollbar-thumb:hover { background: var(--muted-2); }

button { font-family: inherit; }
input, select, textarea { font-family: inherit; color: inherit; }

/* utility */
.mono { font-family: var(--font-mono); font-feature-settings: "tnum", "zero"; }
.serif { font-family: var(--font-serif); }
.uppercase { text-transform: uppercase; letter-spacing: 0.08em; }
.micro { font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 500; }

/* layout */
.app {
  height: 100vh;
  min-height: 0;
  min-width: 1180px;
  display: grid;
  grid-template-rows: var(--header-h) minmax(0, 1fr);
  overflow: hidden;
}

.app-shell {
  display: flex;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

/* live pulse */
@keyframes live-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(47,143,94,0.55); }
  50% { box-shadow: 0 0 0 8px rgba(47,143,94,0); }
}
@keyframes blink-soft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
@keyframes card-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes ticker {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

.skeleton {
  background: linear-gradient(90deg, var(--bg-sunken) 0%, var(--bg-soft) 50%, var(--bg-sunken) 100%);
  background-size: 200% 100%;
  animation: shimmer 1.6s ease-in-out infinite;
  border-radius: var(--r-sm);
}

/* drawer scrim */
.scrim {
  position: fixed; inset: 0;
  background: rgba(20,18,14,0.32);
  backdrop-filter: blur(2px);
  z-index: 80;
  animation: fade-in 0.16s ease-out;
}
@keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes slide-in-r {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
